import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ButtonPrimary,
  usePrevious,
  Popup,
  SidePanelLoader,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onSubmitCard,
  clearOrderCardForm,
  getCardInfo,
} from './store/slices/item';
import { OrderCardFormValues } from './types';
import OrderCardForm from '../../components/cards/order';

import { CardsRoutes } from '../../routes';

const OrderCard = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const { cardId, id, cardEditId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    cards: {
      list: { customers },
      item: {
        user,
        cardSubmitInProgress,
        cardSubmitSuccess,
        cardInfo,
        cardInfoLoading,
      },
    },
    configurationDictionaries: { countries },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<OrderCardFormValues>();

  const physical = watch('physical');

  const prevPhysical = usePrevious(physical);

  const onSubmit = handleSubmit((values) => {
    dispatch(onSubmitCard(values));
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});

    setTimeout(() => {
      dispatch(clearOrderCardForm());

      let link;

      if (cardId) {
        link = generatePath(
          `${CardsRoutes.CARD_INFO_TRANSACTIONS}${search || ''}`,
          {
            cardId,
            id: id as string,
          },
        );
      } else {
        link = `${CardsRoutes.CARDS_LIST}${search || ''}`;
      }

      navigate(link);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (user && cardId) {
      reset({
        userId: user.id,
      });
    }

    if (cardEditId) {
      dispatch(getCardInfo(cardEditId));
    }
  }, [dispatch, user, cardId]);

  useEffect(() => {
    if (cardSubmitSuccess) {
      handleClose();
    }
  }, [cardSubmitSuccess]);

  useEffect(() => {
    if (cardInfo && user && countries) {
      const currentCountry = countries.find(
        (item) => item.value === cardInfo.countryId,
      );

      reset({
        userId: user.id,
        physical: !cardInfo.virtual,
        cardType: cardInfo.virtual ? 'virtual' : 'physical',
        paymentSystem: cardInfo.paymentSystem,
        countryId: cardInfo.countryId || '',
        city: cardInfo.city || '',
        postCode: cardInfo.postCode || '',
        street: cardInfo.street || '',
        apartment: cardInfo.apartment || '',
        countryName: currentCountry ? currentCountry.name : '',
      });
    }
  }, [cardInfo, user, countries]);

  useEffect(() => {
    if (!physical && prevPhysical) {
      setValue('countryId', '');
      setValue('city', '');
      setValue('street', '');
      setValue('apartment', '');
      setValue('postCode', '');
    }
  }, [physical, prevPhysical]);

  let content;

  if (!cardInfoLoading) {
    content = (
      <OrderCardForm
        control={control}
        errors={errors}
        physical={physical}
        customers={customers}
        cardId={cardId}
        countries={countries}
        cardEditId={cardEditId}
        setValue={setValue}
      />
    );
  } else {
    content = <SidePanelLoader />;
  }

  let buttonText;

  if (cardSubmitInProgress) {
    buttonText = 'Loading...';
  } else {
    buttonText = 'Order a card';

    if (cardEditId) {
      buttonText = 'Save';
    }
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Cards}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={cardEditId ? 'Edit card' : 'Order a card'}
        sidepanelExpandedKey="card-edit"
        footer={
          <>
            <ButtonPrimary
              text={buttonText}
              onClick={cardEditId ? handleClose : onSubmit}
              disabled={cardSubmitInProgress}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default OrderCard;
